import { useLazyQuery } from "@apollo/client";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  AtsStckItemEdge,
  AtsStckItemOrderField,
  GetItemsResult,
  GetItemsVars,
  ItemQueryType,
} from "../../masters/items/queries/types";
import { GetItems } from "../../masters/items/queries/queries";

import useUrlParams from "../../../../../customHooks/useUrlParams";
import { EMPTY_STRING, FETCH_MORE_DATA } from "../../../../../utils/constants";
import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import { getModifiedScrollHeight } from "../../../../../utils/UtilFunctions";
import { GridColDef, GridValidRowModel } from "@mui/x-data-grid-pro";
import useLabels from "../../../../../customHooks/useLabels";
import {
  FlexForColumns,
  HEADER_TEXT_ALIGN,
  TABLE_ROW_HEIGHT,
} from "../../../../../styles/Variables";
import { StyledDatagrid } from "../../../../../styles/DatagridStyles";

import { MapsContext } from "../../maps/context/context";
import { mapsPayloadType } from "../../maps/context/reducer";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../../../context/context";
import { PageFrom, empType } from "../../../../../utils/Enum.types";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";

interface Props {
  pageFrom?: PageFrom;
}

const List = ({ pageFrom }: Props) => {
  const navigate = useNavigate();
  const { customer_id, company_id, sub_id, siteID } = useUrlParams();
  const [items, setItems] = useState<AtsStckItemEdge[]>([]);
  const [activeItemId, setActiveItemId] = useState(0);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const { state: globalState } = useContext(AppContext);

  const { user_details } = useLoggedInUserDetails();

  const { dispatch: mapsDispatch, state } = useContext(MapsContext);
  const [GetStockItems, { data, loading, error, fetchMore }] = useLazyQuery<
    GetItemsResult,
    GetItemsVars
  >(GetItems);
  useEffect(() => {
    if (customer_id) {
      GetStockItems({
        variables: {
          after: null,
          customerID: customer_id ? customer_id : 0,
          companyID: company_id
            ? company_id
            : state.companyId
            ? state.companyId
            : 0,
          first: null,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsStckItemOrderField.DESC,
          },

          name: EMPTY_STRING,
          hsnCode: EMPTY_STRING,
          input: {
            query_type: ItemQueryType.All,
            stckCategoryID: 0,
            stckGroupID: 0,
            stckManufacturerID: 0,
            stckUomID: 0,
            stckSubCategoryID: 0,
            ldgrID: 0,
            subsidiaryID: 0,
            isComponent: undefined,
            empID: user_details.id,
          },
        },
      });
    }
  }, [customer_id, GetStockItems, user_details.id]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetItems.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = items.find(
            (company) => company.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setItems(updatedNewData);
      } else {
        setItems(newData);
      }
      setEndCursor(data.GetItems.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges = fetchMoreResult.GetItems.edges;
              const pageInfo = fetchMoreResult.GetItems.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck = prevResult.GetItems.edges.filter(
                ({ node: { id } }) =>
                  newEdges.findIndex(
                    ({ node: { id: newId } }) => newId === id
                  ) !== -1
              );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetItems: {
                  edges: [...items, ...newEdges],
                  pageInfo,
                  totalCount: data ? data.GetItems.totalCount! : 0,
                },
              };
            },
          });
        }
      }
    };
    if (scrollTable) scrollTable.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [items]);

  const rows1: GridValidRowModel[] = useMemo(() => {
    return items.map(({ node: data }, index) => {
      return {
        id: index + 1,
        description: data.desc + data.code,
        ItemId: data.id,
      };
    });
  }, [items]);
  const { itemLabel } = useLabels();
  const columns: GridColDef[] = [
    {
      headerName: itemLabel,
      cellClassName: "td-link",
      field: "description",
      headerAlign: HEADER_TEXT_ALIGN,
      flex: FlexForColumns.DESCRIPTION,
      hideable: false,
    },
  ];
  useEffect(() => {
    if (items.length) {
      setActiveItemId(items[0].node.id);
    }
  }, [items]);
  const showNavbar =
    globalState.empSubType === empType.JCP ||
    globalState.empSubType === empType.PMU ||
    globalState.empSubType === empType.VENDOR_MANAGER ||
    globalState.empSubType === empType.HELP_DESK;
  return (
    <>
      <div className="asset-reports__left--list">
        {items.length ? (
          <StyledDatagrid
            getCellClassName={(params) =>
              params.row.ItemId === activeItemId ? "active" : ""
            }
            columns={columns}
            rows={rows1}
            rowHeight={TABLE_ROW_HEIGHT}
            disableRowSelectionOnClick
            hideFooter
            onRowClick={(params) => {
              // mapsDispatch({
              //   type: mapsPayloadType.ITEM_ID,
              //   payload: {
              //     itemId: params.row.ItemId,
              //   },
              // });

              if (pageFrom === PageFrom.SITE) {
                navigate(
                  `/${customer_id}/company/${company_id}/subsidiary/${sub_id}/site/${siteID}/assetsummary/${params.row.ItemId}`
                );
              } else if (pageFrom === PageFrom.ADDSITE) {
                navigate(`/${customer_id}/assetsummary/${params.row.ItemId}`);
              } else {
                if (showNavbar)
                  navigate(
                    `/${customer_id}/reports/assetsummary/${params.row.ItemId}`
                  );
                else
                  navigate(
                    `/${customer_id}/company/${company_id}/reports/assetsummary/${params.row.ItemId}`
                  );
              }

              setActiveItemId(params.row.ItemId);
            }}
          />
        ) : (
          <>
            <b className="nodata"> No data found</b>
          </>
        )}
      </div>
    </>
  );
};

export default List;
